import * as React from 'react';
import { useNavigate } from 'react-router';
import type {
  ActivityReportingNotificationData,
  Bank,
  BulkImportCompletedNotificationData,
  CommentCCNotificationData,
  CommentNotificationData,
  CommentTaskAssignedNotificationData,
  CrbAddedBeneficialOwnerNotificationData,
  CrbEditedBeneficialOwnerNotificationData,
  CrbEditInfoNotificationData,
  CrbmLicenseStatusUpdateNotificationData,
  CrbmLicenseTransferredNotificationData,
  CrbQuestionnaireRevisionNotificationData,
  CrbUnarchivedNotificationData,
  Dispensary,
  DueDiligenceStatusChangedNotificationData,
  ExpiredDocumentNotificationData,
  ExpiringBusinessLicenseNotificationData,
  ExpiringDocumentNotificationData,
  ExpiringEmployeeLicenseNotificationData,
  FiEditInfoNotificationData,
  FiQuestionnaireCompleteNotificationData,
  MiddeskRegistrationUpdatedNotificationData,
  MiddeskTinUpdatedNotificationData,
  MiddeskWatchlistResultNotificationData,
  MonitoringReviewNeedsReviewNotificationData,
  MsbRegistrationRevokedNotificationData,
  NegativeNewsFoundNotificationData,
  NewAutogeneratedReviewAvailableNotificationData,
  NewDepositNotificationData,
  NewDispensaryToReviewNotificationData,
  NewDocumentUploadedNotificationData,
  NewFincenReportNotificationData,
  NewMarketplaceApplicationNotificationData,
  NewMarketplaceLeadNotificationData,
  NewPayqwickAdminUserNotificationData,
  Notification,
  PosAuthFailureNotificationData,
  QuestionnaireNotificationData,
  RecurringQuestionnaireNotificationData,
  RequestDocumentUploadNotificationData,
  RiskAssessmentAssignedNotificationData,
  SftpParsingFailureNotificationData,
  TaskReassignedNotificationData,
  UpcomingOrOverdueTaskReminderNotificationData,
  UserTaskAssignedNotificationData
} from '@gcv/shared';
import {
  DueDiligenceStatus,
  FincenReportType,
  getCommentDetails,
  isBankUser,
  MainFeatures,
  NotificationTypes
} from '@gcv/shared';
import { Box, useTheme } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { FinCENApi, NotificationsApi } from 'api';
import { getCrbBankStore } from 'stores/CrbBankStore';
import { getCrbDispensaryStore } from 'stores/CrbDispensaryStore';
import { getFiBankStore } from 'stores/FiBankStore';
import { getFiDispensaryStore } from 'stores/FiDispensaryStore';
import { getNotificationStore } from 'stores/NotificationStore';
import { getSnackbarStore } from 'stores/SnackBarStore';
import { getUserStore } from 'stores/UserStore';
import { Button, Drawer } from 'ui';
import { SalesNotificationModal } from 'ui/modules/sales-notification-modal';
import palette from 'ui/theme/palette';
import { DateTimeHelpers } from 'util/dateTime.util';
import {
  goToComment,
  renderNotificationDescription,
  renderNotificationIcon,
  renderNotificationTitle
} from 'util/notifications.util';
import { orgFeatureEnabled } from 'util/org.util';
import { getRiskAssessmentNotificationPath } from 'util/risk-assessment-notification.util';
import { ClearNotificationsModal } from './clear-notifications-modal';
import { ZeroState } from './zero-state';

interface Props {
  org: Bank | Dispensary;
  drawerOpen: boolean;
  setDrawerOpen: (isOpen: boolean) => void;
  setUserCacheData?: () => void;
}

export const NotificationsDrawer: React.FC<Props> = (props) => {
  const { boAsObj } = useFlags();
  const theme = useTheme();
  const navigate = useNavigate();

  const fincenApi = new FinCENApi();
  const notificationsApi = new NotificationsApi();

  const [notification, setNotification] = React.useState<Notification | null>(null);
  const [isSalesDrawerOpen, setIsSalesDrawerOpen] = React.useState(false);
  const [confirmationModal, setConfirmationModal] = React.useState(false);

  const bankStore = getFiBankStore();
  const crbBankStore = getCrbBankStore();
  const crbDispensaryStore = getCrbDispensaryStore();
  const fiDispensaryStore = getFiDispensaryStore();
  const notificationStore = getNotificationStore();
  const snackbarStore = getSnackbarStore();
  const userStore = getUserStore();

  const closeDrawer = () => {
    props.setDrawerOpen(false);
  };

  const handleDownload = async (signedUrl: string) => {
    try {
      const downloadUrl = signedUrl;
      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', downloadUrl);
      linkElement.click();
      linkElement.remove();
    } catch (e) {
      console.log(e);
      snackbarStore.showErrorSnackbarMessage('There was an issue downlading the report.');
    }
  };

  const isCurrentlyOnboarding = (status: DueDiligenceStatus) => {
    return (
      status === DueDiligenceStatus.BANK_IN_PROGRESS ||
      status === DueDiligenceStatus.BANK_PENDING ||
      status === DueDiligenceStatus.GCV_PENDING ||
      status === DueDiligenceStatus.GCV_IN_PROGRESS ||
      status === DueDiligenceStatus.GCV_AWAITING_REVIEW ||
      status === DueDiligenceStatus.GCV_APPROVED
    );
  };

  const goToNotification = async (notification: Notification) => {
    // TODO - GS-16372
    props.setUserCacheData && props.setUserCacheData();

    await notificationsApi.markNotificationSeen(notification.id ? notification.id : '');
    notificationStore.removeNotification(notification.id ? notification.id : '');

    const appType = props.org.orgType === 'bank' ? 'fi' : 'crb';
    const notificationData = notification.data;

    switch (notification.type) {
      case NotificationTypes.ACTIVITY_REPORTING:
        closeDrawer();
        handleDownload((notificationData as ActivityReportingNotificationData).signedS3Path);
        break;
      case NotificationTypes.SFTP_UPLOAD_REPORT:
      case NotificationTypes.SFTP_NO_DATA:
      case NotificationTypes.SFTP_PARSING_FAILURE:
        closeDrawer();
        navigate(
          `/secure/fi/data?tab=imports&reportId=${
            (notificationData as SftpParsingFailureNotificationData).sftpUploadReportId
          }`
        );
        break;
      case NotificationTypes.SFTP_MISSED_SCHEDULE:
        closeDrawer();
        navigate(`/secure/fi/data?tab=imports`);
        break;
      case NotificationTypes.SFTP_ORPHANS_CREATED:
        closeDrawer();
        navigate(`/secure/fi/data?tab=unmatchedTransactions`);
        break;
      case NotificationTypes.COMPLETED_DISPENSARY_REVIEW:
        closeDrawer();
        navigate(`/secure/crb/dashboard`);
        break;
      case NotificationTypes.COMPLETE_EVS:
        closeDrawer();
        navigate(`/secure/crb/identity-verify`);
        break;
      case NotificationTypes.BANKING_PARTNER_IDENTIFIED:
        closeDrawer();
        // TODO: get ID from notification data once it's there
        const status = crbDispensaryStore.getDefaultProvider().templates.value.onboarding.status;
        if (
          status !== DueDiligenceStatus.BANK_AWAITING_REVIEW &&
          status !== DueDiligenceStatus.BANK_REVIEW_IN_PROGRESS &&
          status !== DueDiligenceStatus.BANK_APPROVED
        ) {
          navigate('/secure/crb/found-bank');
        }
        break;
      case NotificationTypes.EXPIRED_BUSINESS_LICENSE:
        closeDrawer();
        navigate(`/secure/crb/company?tab=licenses`);
        break;
      case NotificationTypes.EXPIRED_EMPLOYEE_LICENSE:
        closeDrawer();
        navigate(
          `/secure/crb/${notification.orgId ?? ''}${
            notification.orgId ? '/' : ''
          }my-providers/provider-details/${
            (notificationData as ExpiringEmployeeLicenseNotificationData).bank_id
          }/requirement/employee_licenses`
        );
        break;
      case NotificationTypes.EXPIRING_BUSINESS_LICENSE:
        closeDrawer();
        navigate(
          `/secure/crb/${notification.orgId ?? ''}${
            notification.orgId ? '/' : ''
          }my-providers/provider-details/${
            (notificationData as ExpiringBusinessLicenseNotificationData).bank_id
          }/requirement/business_licenses`
        );
        break;
      case NotificationTypes.EXPIRING_EMPLOYEE_LICENSE:
        closeDrawer();
        navigate(
          `/secure/crb/${notification.orgId ?? ''}${
            notification.orgId ? '/' : ''
          }my-providers/provider-details/${
            (notificationData as ExpiringEmployeeLicenseNotificationData).bank_id
          }/requirement/employee_licenses`
        );
        break;
      case NotificationTypes.EXPIRED_DOCUMENT:
      case NotificationTypes.EXPIRING_DOCUMENT: {
        closeDrawer();
        const docData = notificationData as
          | ExpiringDocumentNotificationData
          | ExpiredDocumentNotificationData;
        if (appType === 'fi') {
          navigate(
            `/secure/fi/accounts/${docData.crb_id}/view/requirement/${docData.requirement_id}?documentId=${docData.document_id}`
          );
        } else {
          navigate(
            `/secure/crb/${notification.orgId ?? ''}${
              notification.orgId ? '/' : ''
            }my-providers/provider-details/${docData.bank_id}/requirement/${
              docData.requirement_id
            }?documentId=${docData.document_id}`
          );
        }
        break;
      }
      case NotificationTypes.REQUEST_DOCUMENT_UPLOAD:
        closeDrawer();
        navigate(
          `/secure/crb/${notification.orgId ?? ''}${
            notification.orgId ? '/' : ''
          }my-providers/provider-details/${
            (notificationData as RequestDocumentUploadNotificationData).bank_id
          }/requirement/${(notificationData as RequestDocumentUploadNotificationData).requirement_id}`
        );
        break;
      case NotificationTypes.SALES_INGESTION_PARTIAL_SUCCESS_RETAIL:
        setNotification(notification);
        setIsSalesDrawerOpen(true);
        break;
      case NotificationTypes.SALES_INGESTION_PARTIAL_SUCCESS_NON_RETAIL:
        setNotification(notification);
        setIsSalesDrawerOpen(true);
        break;
      case NotificationTypes.SALES_INGESTION_FAILURE:
        setNotification(notification);
        setIsSalesDrawerOpen(true);
        break;
      case NotificationTypes.NEW_DISPENSARY_TO_REVIEW:
        closeDrawer();
        const dispId = (notificationData as NewDispensaryToReviewNotificationData).id;
        const disp = [...fiDispensaryStore.dispensaries, ...fiDispensaryStore.dispensariesArchived].find(
          (disp) => disp.id === dispId
        );
        const dueDiligenceStatus = disp?.assigned_onboarding_template.status;
        if (
          dueDiligenceStatus === DueDiligenceStatus.BANK_APPROVED ||
          dueDiligenceStatus === DueDiligenceStatus.BANK_DISCONNECTED
        ) {
          navigate(`/secure/fi/accounts/${dispId}/view`);
          //update_required does not have a routing preference in accounts tab
        } else if (dueDiligenceStatus !== DueDiligenceStatus.UPDATE_REQUIRED) {
          navigate(`/secure/fi/accounts/${dispId}/review`);
        }
        break;
      case NotificationTypes.NEW_DEPOSIT:
        closeDrawer();
        navigate(`/secure/fi/deposits?id=${(notificationData as NewDepositNotificationData).deposit_id}`);
        break;
      case NotificationTypes.NEW_AUTO_GENENRATED_REVIEW_AVAILABLE:
        closeDrawer();
        const reviewId = (notificationData as NewAutogeneratedReviewAvailableNotificationData).reviewId;
        navigate(`/secure/fi/monitoring/review/${reviewId}`);
        break;
      case NotificationTypes.BATCH_NEW_AUTO_GENENRATED_REVIEW_AVAILABLE:
        closeDrawer();
        navigate('/secure/fi/monitoring');
        break;
      case NotificationTypes.MONITORING_REVIEW_NEEDS_REVIEW:
        closeDrawer();
        const needsReviewId = (notificationData as MonitoringReviewNeedsReviewNotificationData).review_id;
        navigate(`/secure/fi/monitoring/review/${needsReviewId}`);
        break;
      case NotificationTypes.MARKETPLACE_NEW_APPLICATION:
        closeDrawer();
        const crbId = (notificationData as NewMarketplaceApplicationNotificationData).crb_id;
        navigate(`/secure/fi/accounts/${crbId}/review`);
        break;
      case NotificationTypes.NEW_DOCUMENT_UPLOADED_FOR_CRB:
        closeDrawer();
        navigate(`/secure/crb/company?tab=documents`);
        break;
      case NotificationTypes.NEW_FINCEN_REPORT:
        closeDrawer();
        const newReportId = (notificationData as NewFincenReportNotificationData).id;

        fincenApi.getReportById(bankStore.bank.id, newReportId).then((report) => {
          if (report.report_type === FincenReportType.SAR) {
            navigate(`/secure/fi/fincen/sar/${newReportId}`);
          } else {
            navigate(`/secure/fi/fincen/ctr/${newReportId}`);
          }
        });
        break;
      case NotificationTypes.NEW_DOCUMENT_UPLOADED:
        closeDrawer();
        const documentData = notificationData as NewDocumentUploadedNotificationData;
        const dispensary = fiDispensaryStore.dispensaries.find(
          (d) => d.name === documentData.dispensary_name
        );
        if (dispensary?.id) {
          if (documentData && documentData.requirement_id) {
            const isActive =
              dispensary.assigned_onboarding_template.status === DueDiligenceStatus.BANK_APPROVED;
            isActive
              ? navigate(
                  `/secure/fi/accounts/${dispensary.id}/view/requirement/${documentData.requirement_id}`
                )
              : navigate(`/secure/fi/accounts/${dispensary.id}/review`);
          } else {
            navigate(`/secure/fi/accounts/${dispensary.id}/view?tab=documents`);
          }
        } else {
          navigate(`/secure/fi/accounts`);
        }
        break;
      case NotificationTypes.COMMENT_MENTION:
      case NotificationTypes.COMMENT_REPLY:
        const data = notificationData as CommentNotificationData;

        const commentUrl = await goToComment(appType, data.commentType, data.idComponents, true);

        if (commentUrl) {
          navigate(commentUrl);
        }

        closeDrawer();
        break;
      case NotificationTypes.COMMENT_CC:
        const ccCommentData = notificationData as CommentCCNotificationData;
        const commentDetails = getCommentDetails(
          'fi',
          ccCommentData.commentType,
          ccCommentData.idComponents,
          undefined,
          undefined,
          DueDiligenceStatus.BANK_APPROVED
        );

        navigate(commentDetails.url);

        closeDrawer();
        break;
      case NotificationTypes.ONE_TIME_QUESTIONNAIRE:
      case NotificationTypes.RECURRING_QUESTIONNAIRE:
      case NotificationTypes.BENEFICIAL_OWNERSHIP_ONE_TIME_QUESTIONNAIRE:
      case NotificationTypes.QUESTIONNAIRE:
        const task_id = (
          notificationData as RecurringQuestionnaireNotificationData | QuestionnaireNotificationData
        )?.task_id;
        // If the task is undefined we want to check if the task has the legacy questionnaire id for it's id
        const questionnaire_id = (notificationData as any)?.questionnaire_result_id;
        if (task_id) {
          navigate(
            `/secure/crb/${
              crbDispensaryStore.currentDispensary.id ? `${crbDispensaryStore.currentDispensary.id}/` : ''
            }inbox?taskId=${task_id}`
          );
        } else if (questionnaire_id) {
          navigate(
            `/secure/crb/${
              crbDispensaryStore.currentDispensary.id ? `${crbDispensaryStore.currentDispensary.id}/` : ''
            }inbox?taskId=${questionnaire_id}`
          );
        }

        break;
      case NotificationTypes.QUESTIONNAIRE_REVISION_REQUESTED:
        const revisionTaskId = (notificationData as CrbQuestionnaireRevisionNotificationData).task_id;
        if (revisionTaskId) {
          navigate(
            `/secure/crb/${
              crbDispensaryStore.currentDispensary.id ? `${crbDispensaryStore.currentDispensary.id}/` : ''
            }inbox?taskId=${revisionTaskId}`
          );
        }
        closeDrawer();
        break;
      case NotificationTypes.CRBM_LICENSE_STATUS_UPDATE:
        closeDrawer();
        const licenseData = notificationData as CrbmLicenseStatusUpdateNotificationData;

        if (appType === 'crb') {
          navigate(
            `/secure/crb/${licenseData.dispensaryId}/my-providers/provider-details/business_licenses?documentId=${licenseData.documentId}`
          );
        } else {
          navigate(
            `/secure/fi/accounts/${licenseData.dispensaryId}/view/requirement/business_licenses?documentId=${licenseData.documentId}`
          );
        }
        break;
      case NotificationTypes.CRBM_LICENSE_TRANSFERRED:
        closeDrawer();
        const { dispensary_id, document_id } = notificationData as CrbmLicenseTransferredNotificationData;

        if (appType === 'crb') {
          navigate(
            `/secure/crb/${dispensary_id}/my-providers/provider-details/business_licenses?documentId=${document_id}`
          );
        } else {
          navigate(
            `/secure/fi/accounts/${dispensary_id}/view/requirement/business_licenses?documentId=${document_id}`
          );
        }
        break;
      case NotificationTypes.FI_QUESTIONNAIRE_COMPLETE:
        closeDrawer();
        navigate(
          `/secure/fi/tasks/questionnaire/overview/${
            (notificationData as FiQuestionnaireCompleteNotificationData).template_id
          }/${(notificationData as FiQuestionnaireCompleteNotificationData).crb_id}/${
            (notificationData as FiQuestionnaireCompleteNotificationData).questionnaire_result_id
          }`
        );
        break;
      case NotificationTypes.POS_AUTH_FAILURE:
        closeDrawer();
        navigate(`/secure/crb/company?tab=integrations`);
        break;
      case NotificationTypes.SERVICE_PROVIDER_POS_AUTH_FAILURE:
        const spPosAuthFailureData = notificationData as PosAuthFailureNotificationData;
        closeDrawer();
        navigate(`/secure/fi/accounts/${spPosAuthFailureData.dispensary_id}/view?tab=activity`);
        break;
      case NotificationTypes.USER_TASK_ASSIGNED:
        closeDrawer();
        const note = notificationData as UserTaskAssignedNotificationData;
        navigate(`/secure/fi/tasks?taskId=${note.task_id}`);
        break;
      case NotificationTypes.RISK_ASSESSMENT_ASSIGNED:
        closeDrawer();
        const assignedAssessment = notificationData as RiskAssessmentAssignedNotificationData;
        navigate(
          getRiskAssessmentNotificationPath(assignedAssessment) ??
            `/secure/fi/tasks?taskId=${assignedAssessment.task_id}`
        );
        break;
      case NotificationTypes.COMMENT_TASK_ASSIGNED:
        closeDrawer();
        const comment = notificationData as CommentTaskAssignedNotificationData;
        if (appType === 'crb') {
          navigate(
            `/secure/crb/${
              crbDispensaryStore.currentDispensary.id ? `${crbDispensaryStore.currentDispensary.id}/` : ''
            }inbox?taskId=${comment.task_id}`
          );
        } else {
          navigate(`/secure/fi/tasks?taskId=${comment.task_id}`);
        }
        break;
      case NotificationTypes.NEW_PAYQWICK_ADMIN_USER:
        closeDrawer();
        const pqAdminData = notificationData as NewPayqwickAdminUserNotificationData;
        navigate(
          `/secure/fi/accounts/${pqAdminData.crbId}/view?tab=${
            boAsObj ? 'people&peopleTab=account-owners' : 'account-owners'
          }`
        );
        break;
      case NotificationTypes.USER_ADDED:
      case NotificationTypes.PROFILE_UPDATED_CRB:
        closeDrawer();
        navigate(`/secure/crb/users`);
        break;
      case NotificationTypes.PROFILE_UPDATED_USER:
        closeDrawer();
        navigate(`/secure/crb/user/settings`);
        break;
      case NotificationTypes.DISCONNECT_CRB:
        closeDrawer();
        navigate(`/secure/crb/my-providers`);
        break;
      case NotificationTypes.UNARCHIVE_CRB:
        closeDrawer();
        const unarchiveData = notificationData as CrbUnarchivedNotificationData;
        navigate(`/secure/crb/my-providers/provider-details/${unarchiveData.service_provider_id}`);
        break;
      case NotificationTypes.CRB_TASK_ASSIGNED:
        closeDrawer();
        const crbTaskData = notificationData as UserTaskAssignedNotificationData;
        navigate(
          `/secure/crb/${
            crbDispensaryStore.currentDispensary.id ? `${crbDispensaryStore.currentDispensary.id}/` : ''
          }inbox?taskId=${crbTaskData.task_id}`
        );
        break;
      case NotificationTypes.FI_EDIT_ADDITIONAL_INFO:
        closeDrawer();
        const fiAdditionalInfoData = notificationData as FiEditInfoNotificationData;
        const serviceProviderAdditonalInfo =
          crbBankStore.banks[fiAdditionalInfoData.fi_id]?.templates.value.onboarding;

        if (serviceProviderAdditonalInfo && isCurrentlyOnboarding(serviceProviderAdditonalInfo.status)) {
          const templateId = serviceProviderAdditonalInfo.template_id;
          const templateResultId = serviceProviderAdditonalInfo.template_result_id;

          navigate(
            `/secure/crb/provider-onboarding/bank/${fiAdditionalInfoData.fi_id}/template/${templateId}/result/${templateResultId}?step=additional-information`
          );
        } else {
          navigate(
            `/secure/crb/${fiAdditionalInfoData.crb_id}/my-providers/provider-details/${fiAdditionalInfoData.fi_id}?tab=additional-information`
          );
        }
        break;
      case NotificationTypes.FI_EDIT_BUSINESS_DETAILS:
        closeDrawer();
        const fiBusinessDetailsData = notificationData as FiEditInfoNotificationData;
        const serviceProviderBusinessDetails =
          crbBankStore.banks[fiBusinessDetailsData.fi_id]?.templates.value.onboarding;

        if (serviceProviderBusinessDetails && isCurrentlyOnboarding(serviceProviderBusinessDetails.status)) {
          const templateId = serviceProviderBusinessDetails.template_id;
          const templateResultId = serviceProviderBusinessDetails.template_result_id;
          navigate(
            `/secure/crb/provider-onboarding/bank/${fiBusinessDetailsData.fi_id}/template/${templateId}/result/${templateResultId}?step=business-details`
          );
        } else {
          navigate(`/secure/crb/company`);
        }
        break;
      case NotificationTypes.FI_EDIT_OPERTATION_DETAILS:
        closeDrawer();
        const fiOperationDetailsData = notificationData as FiEditInfoNotificationData;
        const serviceProviderOperationalDetails =
          crbBankStore.banks[fiOperationDetailsData.fi_id]?.templates.value.onboarding;

        if (
          serviceProviderOperationalDetails &&
          isCurrentlyOnboarding(serviceProviderOperationalDetails.status)
        ) {
          const templateId = serviceProviderOperationalDetails.template_id;
          const templateResultId = serviceProviderOperationalDetails.template_result_id;

          navigate(
            `/secure/crb/provider-onboarding/bank/${fiOperationDetailsData.fi_id}/template/${templateId}/result/${templateResultId}?step=operational-details`
          );
        } else {
          navigate(`/secure/crb/company?tab=operational`);
        }

        break;
      case NotificationTypes.CRB_EDIT_ADDITIONAL_INFO:
        closeDrawer();
        const crbAdditionalInfoData = notificationData as CrbEditInfoNotificationData;
        navigate(`/secure/fi/accounts/${crbAdditionalInfoData.crb_id}/view?tab=additional-info`);
        break;
      case NotificationTypes.CRB_EDIT_BUSINESS_DETAILS:
        closeDrawer();
        const crbBusinessDetailsData = notificationData as CrbEditInfoNotificationData;
        navigate(`/secure/fi/accounts/${crbBusinessDetailsData.crb_id}/view?tab=general`);
        break;
      case NotificationTypes.CRB_EDIT_OPERTATION_DETAILS:
        closeDrawer();
        const crbOperationDetailsData = notificationData as CrbEditInfoNotificationData;
        navigate(`/secure/fi/accounts/${crbOperationDetailsData.crb_id}/view?tab=operational`);
        break;
      case NotificationTypes.FI_ADDED_BENEFICIAL_OWNER:
      case NotificationTypes.FI_EDITED_BENEFICIAL_OWNER:
        closeDrawer();
        navigate(`/secure/crb/company?tab=beneficial-owners`);
        break;
      case NotificationTypes.CRB_ADDED_BENEFICIAL_OWNER:
      case NotificationTypes.CRB_EDITED_BENEFICIAL_OWNER:
        closeDrawer();
        const crbEditedBeneficialOwnerData = notificationData as
          | CrbAddedBeneficialOwnerNotificationData
          | CrbEditedBeneficialOwnerNotificationData;
        navigate(
          `/secure/fi/accounts/${crbEditedBeneficialOwnerData.crb_id}/view?tab=people&peopleTab=beneficial-owners`
        );
        break;
      case NotificationTypes.OTHER_SP_EDIT_BUSINESS_DETAILS:
        closeDrawer();
        const fiOtherBusinessDetailsData = notificationData as CrbEditInfoNotificationData;
        navigate(`/secure/fi/accounts/${fiOtherBusinessDetailsData.crb_id}/view?tab=general`);
        break;
      case NotificationTypes.OTHER_SP_EDIT_OPERTATION_DETAILS:
        closeDrawer();
        const fiOtherOperationDetailsData = notificationData as CrbEditInfoNotificationData;
        navigate(`/secure/fi/accounts/${fiOtherOperationDetailsData.crb_id}/view?tab=operational`);
        break;
      case NotificationTypes.ONBOARD_WITH_TRACE_DRAWER:
        closeDrawer();
        navigate(`/secure/crb/company?tab=integrations`);
        break;
      case NotificationTypes.NEGATIVE_NEWS_FOUND:
        closeDrawer();
        const negativeNewsData = notificationData as NegativeNewsFoundNotificationData;
        navigate(negativeNewsData.url);
        break;
      case NotificationTypes.UPCOMING_OR_OVERDUE_TASK_REMINDER:
        closeDrawer();

        const upcomingOrOverdueTaskData = notificationData as UpcomingOrOverdueTaskReminderNotificationData;
        const user = getUserStore().user;

        if (user && isBankUser(user)) {
          const reminderAssessmentUrl = getRiskAssessmentNotificationPath(upcomingOrOverdueTaskData);

          navigate(reminderAssessmentUrl ?? `/secure/fi/tasks?taskId=${upcomingOrOverdueTaskData.task_id}`);
        } else {
          const crbStore = getCrbDispensaryStore();
          const crb = crbStore.currentDispensary;

          navigate(
            `/secure/crb/${crb?.id ?? ''}${crb?.id ? '/' : ''}inbox?taskId=${
              upcomingOrOverdueTaskData.task_id
            }`
          );
        }

        break;
      case NotificationTypes.TASK_REASSIGNED:
        closeDrawer();

        const taskReassignedData = notificationData as TaskReassignedNotificationData;
        const taskReassignedUser = getUserStore().user;

        if (taskReassignedUser && isBankUser(taskReassignedUser)) {
          navigate(`/secure/fi/tasks?taskId=${taskReassignedData.task_id}`);
        } else {
          const crbStore = getCrbDispensaryStore();
          const crb = crbStore.currentDispensary;

          navigate(
            `/secure/crb/${crb?.id ?? ''}${crb?.id ? '/' : ''}inbox?taskId=${taskReassignedData.task_id}`
          );
        }

        break;
      case NotificationTypes.MIDDESK_REGISTRATION_UPDATED:
      case NotificationTypes.MIDDESK_TIN_UPDATED:
      case NotificationTypes.MIDDESK_WATCHLIST_RESULT:
        closeDrawer();

        const registrationData = notificationData as
          | MiddeskRegistrationUpdatedNotificationData
          | MiddeskTinUpdatedNotificationData
          | MiddeskWatchlistResultNotificationData;
        const registrationDispensary = fiDispensaryStore.findDispensary(registrationData.dispensary_id);
        if (registrationDispensary) {
          const isOnboarding = isCurrentlyOnboarding(
            registrationDispensary.assigned_onboarding_template.status
          );
          isOnboarding
            ? navigate(`/secure/fi/accounts/${registrationDispensary.id}/review`)
            : navigate(`/secure/fi/accounts/${registrationDispensary.id}/view?tab=general`);
        } else {
          navigate(`/secure/fi/accounts`);
        }

        break;
      case NotificationTypes.NEW_MARKETPLACE_LEAD:
        const newMarketplaceLeadData = notificationData as NewMarketplaceLeadNotificationData;
        navigate(`/secure/fi/accounts?tab=leads&leadId=${newMarketplaceLeadData.lead_id}`);
        break;
      case NotificationTypes.MARKETPLACE_LEAD_STATUS_CHANGED:
        navigate(`/secure/crb/marketplace`);
        break;
      case NotificationTypes.SEEDLING_QUESTIONNAIRE_COMPLETED:
        closeDrawer();
        const seedlingQuestionnaireData = notificationData as { crb_id?: string };
        if (seedlingQuestionnaireData?.crb_id) {
          navigate(`/secure/fi/accounts/${seedlingQuestionnaireData.crb_id}/view`);
        } else {
          navigate(`/secure/fi/accounts`);
        }
        break;
      case NotificationTypes.TEMPLATE_SWAP_COMPLETED:
        closeDrawer();
        const templateSwapData = notificationData as { task_id?: string };
        if (templateSwapData?.task_id) {
          const crbId = crbDispensaryStore.currentDispensary?.id ?? '';
          navigate(`/secure/crb/${crbId}${crbId ? '/' : ''}inbox?taskId=${templateSwapData.task_id}`);
        }
        break;
      case NotificationTypes.DUE_DILIGENCE_STATUS_CHANGED:
        closeDrawer();
        const dueDiligenceStatusChangedData = notificationData as DueDiligenceStatusChangedNotificationData;
        if (dueDiligenceStatusChangedData.task_id) {
          navigate(
            `/secure/crb/${dueDiligenceStatusChangedData.crb_id}/inbox?taskId=${dueDiligenceStatusChangedData.task_id}`
          );
        } else {
          navigate(
            `/secure/crb/${dueDiligenceStatusChangedData.crb_id}/my-providers/provider-details/${dueDiligenceStatusChangedData.fi_id}`
          );
        }
        break;
      case NotificationTypes.MSB_REGISTRATION_REVOKED:
        closeDrawer();
        const msbRevokedData = notification.data as MsbRegistrationRevokedNotificationData;
        navigate(
          `/secure/fi/accounts/${msbRevokedData.crb_id}/view/requirement/${msbRevokedData.requirement_id}?documentId=${msbRevokedData.document_id}`
        );
        break;
      case NotificationTypes.BULK_IMPORT_COMPLETED:
        closeDrawer();
        const bulkImportData = notification.data as BulkImportCompletedNotificationData;
        navigate(`/secure/fi/accounts/bulk-imports?importId=${bulkImportData.import_id}`);
        break;
      default:
        break;
    }
  };

  return (
    <Drawer
      width="small"
      id="notifications-drawer"
      title="Notifications"
      anchor="right"
      open={props.drawerOpen}
      onClose={() => closeDrawer()}
      bottomActionMargin="0 0 55px 0"
      bottomAction={
        notificationStore.notifications.length > 0 && (
          <Button
            color="default-outlined"
            onClick={() => {
              setConfirmationModal(true);
            }}
            label="Clear all"
          />
        )
      }
    >
      {notificationStore.notifications.length === 0 && (
        <ZeroState
          width="336px"
          title={'There are no new notifications'}
          icon="/img/empty-states/NoNotifications.svg"
        />
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ overflow: 'auto' }}>
          {Object.values(
            notificationStore.notifications.reduce<{ [key: string]: Notification[] }>((acc, curr) => {
              // group the types into categories then flatten into single array and map
              const userCanViewDeposits = orgFeatureEnabled(bankStore.bank, MainFeatures.Deposits);
              if (
                !userCanViewDeposits &&
                (curr.type === NotificationTypes.MAX_DEPOSIT_UPDATED ||
                  curr.type === NotificationTypes.NEW_DEPOSIT)
              ) {
                return acc;
              }
              const category = renderNotificationTitle(curr);
              if (category) {
                (acc[category] = acc[category] || []).push(curr);
              }
              return acc;
            }, {})
          )
            .flat()
            .sort((a, b) => {
              const dateA = DateTimeHelpers.parseFromISOString(a.created_date, props.org.iana_timezone);
              const dateB = DateTimeHelpers.parseFromISOString(b.created_date, props.org.iana_timezone);
              if (dateA > dateB) {
                return 1;
              } else if (dateA < dateB) {
                return -1;
              }
              return 0;
            })
            .map((n) => {
              return (
                <Box
                  key={n.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    borderBottom: `1px solid ${palette.background.secondary}`,
                    cursor: 'pointer',
                    justifyContent: 'space-around',
                    padding: '14px',
                    '&:hover': {
                      backgroundColor: (theme) => theme.palette.primary.light
                    }
                  }}
                  onClick={() => goToNotification(n)}
                >
                  <Box>{renderNotificationIcon(n.type)}</Box>
                  <Box style={{ width: '285px' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        fontSize: '16px',
                        fontWeight: 700,
                        lineHeight: '24px',
                        color: (theme) => theme.palette.text.primary
                      }}
                    >
                      <Box style={{ flexGrow: 1 }}>{renderNotificationTitle(n)}</Box>
                      <Box
                        sx={{
                          fontSize: '10px',
                          fontWeight: 700,
                          color: (theme) => theme.palette.text.secondary,
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                          ml: 1
                        }}
                      >
                        {DateTimeHelpers.formatISOToFriendlyDateString(
                          n.created_date,
                          props.org.iana_timezone
                        )}
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        fontSize: '13px',
                        lineHeight: '17px',
                        fontWeight: 500,
                        color: (theme) => theme.palette.text.secondary,
                        whiteSpace: 'normal',
                        wordBreak: 'break-word'
                      }}
                    >
                      {renderNotificationDescription(n as Notification)}
                    </Box>
                  </Box>
                </Box>
              );
            })}
        </Box>
      </Box>

      <SalesNotificationModal
        isOpen={isSalesDrawerOpen}
        onClose={() => {
          setIsSalesDrawerOpen(false);
          setTimeout(() => {
            setNotification(null);
          }, 100);
        }}
        onComplete={() => {
          setIsSalesDrawerOpen(false);
          setTimeout(() => {
            setNotification(null);
          }, 100);
        }}
        notification={notification}
      />

      <ClearNotificationsModal
        isOpen={confirmationModal}
        onClose={() => {
          setConfirmationModal(false);
        }}
        onSubmit={() => {
          notificationsApi.clearAllNotifications(props.org.id, userStore.user.id);
          notificationStore.clearStore();
          setConfirmationModal(false);
          closeDrawer();
        }}
      />
    </Drawer>
  );
};
